<script setup lang="ts">
import {
  BookOutlined,
  ClockCircleOutlined,
  ShareAltOutlined,
  PlayCircleFilled
} from '@ant-design/icons-vue'

import { useAuthStore } from '~~/stores/auth'
import { useCoursesStore } from '~~/stores/courses'

const router = useRouter()
const route = useRoute()

const emits = defineEmits(['preview'])
const { addCdnToUrl } = useFormatUrlCdn()
const { $currencyFormat, $convertToHour } = useNuxtApp()
const authStore = useAuthStore()
const { getCertificate } = useGenerateCertificate()
const coursesStore: Record<string, any> = useCoursesStore()
const courseDetail = computed(() => coursesStore.state.courseDetail)

const previewThumbnail = ref<boolean>(false)
const showShare = ref<boolean>(false)
const showBuyCourse = ref<boolean>(false)
const showSuccessPayment = ref<boolean>(false)

const redirectToLogin = () => {
  router.push({
    path: '/signin',
    query: {
      next: route?.path
    }
  })
}

const buyCourseTrigger = () => {
  if (authStore.state?.isLoggedIn) {
    showBuyCourse.value = true
  } else {
    redirectToLogin()
  }
}

const moveToStartMateri = () => {
  const section = courseDetail.value?.data?.sections[0]
  router.push({
    path: `${route?.path}/materi`,
    query: {
      courseSerial: courseDetail.value?.data?.serial,
      sectionSerial: section?.serial,
      contentSerial: section?.contents[0]?.serial
    }
  })
}

const triggerWishlist = (courseSerial: string) => {
  if (authStore.state?.isLoggedIn) {
    coursesStore.sendWishlist({
      payload: {
        courseSerial,
        wishlist: true
      }
    })
  } else {
    redirectToLogin()
  }
}

const handlePaymentSuccess = () => {
  showBuyCourse.value = false
  showSuccessPayment.value = true
}

const handleCancelPaymentSuccess = () => {
  showSuccessPayment.value = false
}

const setPaymentStatusInfo = () => {
  const { payment } = route?.query
  switch (payment) {
    case 'success':
      showSuccessPayment.value = true
      break
    case 'failed':
      message.error(
        'Proses pembayaran telah gagal, silahkan cek transaksi atau hubungi administrator'
      )
      break
    default:
      showSuccessPayment.value = false
      break
  }
}

onMounted(() => {
  setPaymentStatusInfo()
})

watch(
  () => previewThumbnail.value,
  (newVal) => {
    emits('preview', newVal)
  }
)
</script>

<template>
  <aside class="kursus-detail__sticky-wrapper">
    <div class="kursus-detail__sticky-thumbnail banner-image__mobile">
      <img
        loading="lazy"
        :src="addCdnToUrl(courseDetail?.data?.thumbnailURL)"
        :alt="courseDetail?.data?.title ?? '-'"
      />
      <div
        v-if="courseDetail?.data?.videoPreviewAttachmentMetadata"
        class="thumbnail-overlay"
      >
        <div
          class="thumbnail-overlay__icon"
          @click.prevent="previewThumbnail = true"
        >
          <PlayCircleFilled />
        </div>
      </div>
    </div>

    <div class="kursus-detail__sticky-price">
      <h3 class="price-current">
        Rp
        {{
          $currencyFormat(
            courseDetail?.data?.priceDiscount > 0
              ? courseDetail?.data?.priceDiscount
              : courseDetail?.data?.priceNormal || 0
          )
        }}
      </h3>
      <h3
        v-if="
          courseDetail?.data?.priceNormal &&
          courseDetail?.data?.priceNormal > 0 &&
          courseDetail?.data?.priceDiscount > 0
        "
        class="price-discount"
      >
        Rp {{ $currencyFormat(courseDetail?.data?.priceNormal ?? 0) }}
      </h3>
    </div>

    <div class="px-4 mb-6">
      <a-button
        v-if="courseDetail?.isLoading"
        type="primary"
        :loading="courseDetail?.isLoading"
      >
        Loading...
      </a-button>
      <a-button
        v-else-if="!courseDetail?.isLoading && !courseDetail?.data?.isEnrolled"
        type="primary"
        size="large"
        block
        class="mb-4"
        @click="buyCourseTrigger"
      >
        Beli Kelas
      </a-button>
      <a-button
        v-else
        type="primary"
        size="large"
        block
        @click="moveToStartMateri"
      >
        Mulai Belajar
      </a-button>
      <a-button
        v-if="
          !courseDetail?.isLoading &&
          courseDetail?.data?.isPlaylistCourseAccomplished
        "
        type="primary"
        size="large"
        class="mt-4"
        block
        @click="getCertificate(courseDetail?.data)"
      >
        Download Sertifikat
      </a-button>
    </div>

    <div class="kursus-detail__sticky-info">
      <div class="info-item item-title">Kamu akan mendapatkan</div>
      <ul>
        <li>
          <div class="info-item">
            <ClockCircleOutlined />
            <div>
              {{
                courseDetail?.data?.totalCourseDuration
                  ? `${$convertToHour(
                      courseDetail?.data?.totalCourseDuration
                    )} durasi belajar`
                  : '-'
              }}
            </div>
          </div>
        </li>
        <li>
          <div class="info-item">
            <BookOutlined />
            <div>Kuis</div>
          </div>
        </li>
        <li>
          <div v-if="courseDetail?.data?.hasCertificate" class="info-item">
            <AtomsIconCertificate />
            <div>Sertifikat kelulusan</div>
          </div>
        </li>
        <li>
          <div class="info-item">
            <AtomsIconInfinity />
            <div>Akses selamanya</div>
          </div>
        </li>
      </ul>
    </div>

    <div class="w-full flex justify-between items-center mb-6 px-4">
      <a-button
        type="text"
        class="flex items-center"
        block
        @click="triggerWishlist(courseDetail?.data?.serial)"
      >
        <HeartOutlined /> Wishlist
      </a-button>
      <a-divider
        type="vertical"
        class="rounded-xl"
        style="border-left-width: 2px; height: 32px; color: var(--primary-200)"
      />
      <a-button
        type="text"
        class="flex items-center"
        block
        @click="showShare = true"
      >
        <ShareAltOutlined /> Bagikan
      </a-button>
    </div>

    <LazyModulesKursusDetailMoleculesShare
      :show="showShare"
      @cancel="showShare = false"
    />

    <LazyModulesKursusDetailMoleculesModalBuyCourse
      :show="showBuyCourse"
      @success="handlePaymentSuccess"
      @cancel="showBuyCourse = false"
    />

    <LazyModulesKursusDetailMoleculesPaymentSuccess
      :show="showSuccessPayment"
      @cancel="handleCancelPaymentSuccess"
    />

    <ClientOnly>
      <LazyAtomsModal
        v-model:visible="previewThumbnail"
        :width="788"
        class="preview-player"
        centered
        :closable="false"
        @cancel="previewThumbnail = false"
      >
        <div class="preview-player__content">
          <a-spin class="preview-player__preload" size="large" />
          <div id="jwplayercontent" />
        </div>
      </LazyAtomsModal>
    </ClientOnly>
  </aside>
</template>

<style lang="scss">
@use './styles.scss';
</style>
